import React, { useState } from "react";

import credentialingDashboardApi from "apis/credentialingDashboard";
import npiDocumentsApi from "apis/npi_documents";
import { SubHeader, PageLoader, Modal } from "clearsense-ui/components";
import { useUserState } from "contexts/user";
import { getPermissions } from "utils/roleBasedAccess";

import NPIRecordsList from "./NPIRecordsList";
import PrimaryTabs from "./PrimaryTabs";
import SearchNPI from "./SearchNPI";

const CredentialingDashboard = ({ features }) => {
    const [searchParams, setSearchParams] = useState({});
    const [data, setData] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const [isNPILoading, setIsNPILoading] = useState(false);
    const [npiRecords, setNpiRecords] = useState([]);
    const [isViewPdf, setIsViewPdf] = useState(false);
    const [currentPdfName, setCurrentPdfName] = useState();
    const [isPDFPresent, setIsPDFPresent] = useState(false);
    const [isViewMode, setIsViewMode] = useState(false);
    const user = useUserState();
    const userPermissions = getPermissions(features, user?.role);

    const handleSearch = async (values) => {
        setSearchParams({ credentialing_dashboard: { npi: values.npi } });
        setIsNPILoading(true);
        try {
            const { data } = await credentialingDashboardApi.fetchNPIRecords(
                values.npi
            );
            setNpiRecords(data.primary);
        } catch (err) {
            logger.log(err);
        } finally {
            setIsNPILoading(false);
        }
    };

    const handleEditNPI = async ({
        npi,
        rec_create_dt,
        subject_id,
        caqh_providerid,
        evidence_file,
    }) => {
        try {
            setIsLoading(true);
            setIsPDFPresent(false);
            const params = {
                credentialing_dashboard: {
                    npi,
                    rec_create_dt,
                    subject_id,
                    caqh_providerid,
                },
            };
            setSearchParams(params);
            const { data } = await credentialingDashboardApi.fetchData(params);
            setData({
                primary: data?.primary,
                office: data?.office,
                board_certification: data?.board_certification,
                education: data?.education,
                state_lic: data?.state_lic,
                dea: data?.dea,
                medicaid: data?.medicaid,
                hospital: data?.hospital,
                accreditation: data?.accreditation,
                insurance: data?.insurance,
                work_history: data?.work_history,
                product: data?.product,
                notes: data?.primary?.map((item) =>
                    (({
                        is_data_validated,
                        is_provider_no_longer_participating,
                        notes,
                    }) => ({
                        is_data_validated,
                        is_provider_no_longer_participating,
                        notes,
                    }))(item)
                ),
            });

            evidence_file && setCurrentPdfName(evidence_file);
            await checkIfPDFExist(evidence_file);
        } catch (error) {
            logger.log(error);
        } finally {
            setIsLoading(false);
        }
    };

    const checkIfPDFExist = async (pdfName) => {
        if (!pdfName) return;
        try {
            const { data } = await npiDocumentsApi.show(pdfName);
            !data?.errors && setIsPDFPresent(true);
        } catch (err) {
            logger.log(err);
        }
    };

    return (
        <div className="cs-ui-container px-8 space-y-8">
            <div>
                <SubHeader
                    breadcrumbs={[
                        { label: "Credentialing Edit UI", link: "#" },
                    ]}
                    className="mt-8"
                />
            </div>
            <SearchNPI
                isNPILoading={isNPILoading}
                handleSearch={handleSearch}
            />
            {!isNPILoading && searchParams?.credentialing_dashboard?.npi && (
                <NPIRecordsList
                    npiRecords={npiRecords}
                    userPermissions={userPermissions}
                    isViewMode={isViewMode}
                    searchParams={searchParams}
                    handleEditNPI={handleEditNPI}
                    setIsViewMode={setIsViewMode}
                />
            )}
            {isLoading ? (
                <PageLoader />
            ) : (
                searchParams?.credentialing_dashboard?.rec_create_dt && (
                    <PrimaryTabs
                        data={data}
                        searchParams={searchParams}
                        isPDFPresent={isPDFPresent}
                        isViewMode={isViewMode}
                        userPermissions={userPermissions}
                        currentPdfName={currentPdfName}
                        updateData={handleEditNPI}
                        setIsViewPdf={setIsViewPdf}
                    />
                )
            )}
            <Modal
                isOpen={isViewPdf}
                closeButton={false}
                data-cy="archival-documents-view-pdf-modal"
                className="archival-reports__modal"
                onClose={() => setIsViewPdf(false)}
            >
                <object
                    data={`/api/v1/npi_documents/${currentPdfName}`}
                    width="100%"
                    height="100%"
                >
                    <div className="grid h-full place-items-center">
                        Couldn't load the file!
                    </div>
                </object>
            </Modal>
        </div>
    );
};

export default CredentialingDashboard;
